import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getOrganizationId } from '../store/selectors/organization.selector';

/**
 * Drops every query the previous organization filled. Queries nobody renders are
 * removed; the ones on screen are reset and fetched again. `queryClient.clear()`
 * would cancel their fetch in flight, and react-query never retries a cancelled
 * fetch, so a widget that was loading during the switch would spin for good.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - The app's client.
 * @returns {Promise<void>} Resolves once the rendered queries fetched again.
 * @example
 * await dropOrganizationQueries(queryClient);
 */
export const dropOrganizationQueries = queryClient => {
  queryClient.removeQueries({ type: 'inactive' });
  return queryClient.resetQueries({ type: 'active' });
};

// Server data is org-scoped, but the query keys aren't org-keyed — so on an org
// switch react-query would serve the previous org's cached data until staleTime.
// Drop the whole cache whenever the active org changes (also covers logout, when
// the id clears). Mirrors the Redux APPLICATION_WIPE_DATA reset.
//
// Safety assumption (this hook is the cross-org-bleed safety net): the host App
// stays mounted across logout, so `prevOrgId` tracks the full org→null→org
// lifecycle. If a host ever *remounts* this hook while orgId is transiently null,
// the ref re-initializes to null and a later null→org switch would skip the clear
// below — potentially leaking the previous session's cache into the next one.
// Believed unreachable today; keep this mounted at a stable App-level boundary.
export const useClearQueryCacheOnOrgChange = () => {
  const queryClient = useQueryClient();
  const orgId = useSelector(getOrganizationId);
  const prevOrgId = useRef(orgId);

  useEffect(() => {
    if (prevOrgId.current !== orgId) {
      // Skip the initial hydration transition (undefined/null → first org): the
      // cache is empty then. Only drop on a real org switch or on logout
      // (org → null) — both have a truthy previous id.
      if (prevOrgId.current) {
        dropOrganizationQueries(queryClient);
      }
      prevOrgId.current = orgId;
    }
  }, [orgId, queryClient]);
};
